import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { MaterialLibraryModule } from '@cdw-ae/common-frontend-materials';
import { CdwIconModule } from '@cdw-ae/common-ui-components-icon';
import {
  CdwColDef,
  CdwSsrmGridComponent,
} from '@cdw-ae/common-ui-components-search-grid';
import type { FilterBarField } from '@cdw-ae/filter-bar-interfaces';
import { FilterBarTextMatchersModule } from '@cdw-ae/filter-bar-text-matchers';
import { SnackbarService } from '@cdw-ae/snackbar';
import { Store } from '@ngrx/store';
import { Subject, take, takeUntil } from 'rxjs';
import {
  selectCustomerId,
  selectDbUserId,
  selectFbUserId,
  selectUserEmail,
  selectUserName,
} from '../../../../../+state/user-identity/user-identity-settings.selectors';
import { TailwindColors } from '../../../../../common/constants/tailwind-colors';
import {
  DeviceExportImportService,
  type DialogIdentity,
} from '../../../../../common/services/device/device-export-import.service';
import { NavbarActionButtonService } from '../../navbar-action-buttons/services/navbar-action-button.service';
import {
  getColumnsForAssetType,
  getFiltersForAssetType,
  getItemLabelForAssetType,
} from './asset-change-columns.config';
import { AssetChangeDataService } from './asset-change-data.service';

@Component({
  selector: 'audit-log-job-detail',
  standalone: true,
  imports: [
    CommonModule,
    MaterialLibraryModule,
    CdwIconModule,
    CdwSsrmGridComponent,
    MatIconModule,
    FilterBarTextMatchersModule,
  ],
  providers: [AssetChangeDataService],
  templateUrl: './audit-log-job-detail.component.html',
  styleUrl: './audit-log-job-detail.component.scss',
})
export class AuditLogJobDetailComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  public readonly gray50 = TailwindColors.gray50;

  public columnDefs: CdwColDef[] = [];
  public filterBarSettings: FilterBarField[] = [];
  public componentId = 'AuditLogJobDetailComponent';
  public itemLabel = 'asset';
  public actionName = '';
  public isReady = false;

  // EDUS-2698: source audit log id + user identity for the "Export" button.
  private auditLogId?: number;
  private customerId = '';
  private fbUserId = '';
  private dbUserId = 0;
  private userEmail = '';
  private userName?: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public assetChangeDataService: AssetChangeDataService,
    private navbarButtons: NavbarActionButtonService,
    private store: Store,
    private deviceExportImport: DeviceExportImportService,
    private snackbarService: SnackbarService
  ) {}

  ngOnInit(): void {
    this.registerExportButton();
    this.subscribeToIdentity();

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((qp) => {
      const auditLogId = parseInt(qp.auditLogId, 10);
      const assetTypeId = parseInt(qp.assetTypeId, 10);
      this.actionName = qp.actionName || '';

      if (auditLogId && assetTypeId) {
        // Persist for the export flow (the audit-log-changes export resolves the
        // source device-update job from this id).
        this.auditLogId = auditLogId;
        this.assetChangeDataService.setContext(auditLogId, assetTypeId);
        this.columnDefs = getColumnsForAssetType(assetTypeId);
        this.filterBarSettings = getFiltersForAssetType(assetTypeId);
        this.itemLabel = getItemLabelForAssetType(assetTypeId);
        this.isReady = true;
      }
    });
  }

  private subscribeToIdentity(): void {
    this.store
      .select(selectCustomerId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => {
        this.customerId = id;
      });
    this.store
      .select(selectFbUserId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((uid) => {
        this.fbUserId = uid;
      });
    this.store
      .select(selectDbUserId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => {
        this.dbUserId = id;
      });
    this.store
      .select(selectUserEmail)
      .pipe(takeUntil(this.destroy$))
      .subscribe((email) => {
        this.userEmail = email;
      });
    this.store
      .select(selectUserName)
      .pipe(takeUntil(this.destroy$))
      .subscribe((name) => {
        this.userName = name || undefined;
      });
  }

  ngOnDestroy(): void {
    // AuditLogComponent stashes the drawer it should reopen when the user comes
    // back from here. Leaving for anywhere else must drop it, or the next visit
    // to the audit log pops a stale drawer (with the user's name and email
    // still sitting in sessionStorage).
    const target = this.router.getCurrentNavigation()?.finalUrl?.toString() ?? '';
    if (target.split('?')[0] !== '/administration/audit-log') {
      sessionStorage.removeItem('auditLogReturnDrawer');
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  onGridLoadError(error: Error): void {
    this.snackbarService.showError(
      error?.message
        ? `Failed to load changes: ${error.message}`
        : 'Failed to load changes.'
    );
  }

  private registerExportButton(): void {
    this.navbarButtons.registerButton({
      id: 'export-devices',
      label: 'Export',
      level: 'primary',
      icon: 'file_export',
      iconSize: '25px',
      iconPosition: 'left',
      useMatIcon: false,
      buttonWidth: '180px',
      mode: 'icon-pinned-left',
      visibleOnRoutes: ['administration/audit-log/job'],
      order: 1,
      isDisabled: () => this.auditLogId == null,
      onClick: () => this.exportAssets(),
    });
  }

  private exportAssets(): void {
    const auditLogId = this.auditLogId;
    if (auditLogId == null) {
      return;
    }

    const identity: DialogIdentity = {
      fbUserId: this.fbUserId,
      dbUserId: this.dbUserId,
      userEmail: this.userEmail,
      userName: this.userName,
      customerId: parseInt(this.customerId, 10),
    };

    // The export covers every change in the audit log whatever the grid's
    // filters, so the dialog's row count (and its Sheets cell-limit check)
    // needs the unfiltered total, not the grid's.
    this.assetChangeDataService
      .fetchSqlData({
        activeFilters: [],
        supplementalFilters: [],
        startRow: 0,
        endRow: 1,
      })
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: ({ itemTotal }) => {
          this.deviceExportImport.openAuditLogChangesExportDialog({
            auditLogId,
            identity,
            totalItemCount: itemTotal,
            exportName: 'Device Changes',
          });
        },
        error: (error: Error) => {
          this.snackbarService.showError(
            error?.message
              ? `Failed to start export: ${error.message}`
              : 'Failed to start export.'
          );
        },
      });
  }

  goBack(): void {
    const currentParams = this.route.snapshot.queryParams;
    this.router.navigate(['/administration/audit-log'], {
      queryParams: currentParams,
    });
  }
}
